'use client'
// LEGACY-STD variant (2026-07-18): a faithful React translation of the
// original legacy std answers/msges row design (php/html/subs/std/templates/
// li_msg.php + css/test.css). ONE component with the same is_msg branch the
// legacy template used:
//   named msg  -> blue #3472a9 namekey strip, headline subject LABEL, both
//                 subs links (answers + msges)
//   answer     -> checkbox only, headline from-chip + title link, answers
//                 link only (answers never host named msges)
// Shared geometry: gray #f5f5f5 text block with the 39px silver avatar,
// #ddd-underlined headline with the right-aligned fav/qr/time group, 12px
// body with the "... read more / Hide more" expander (full body rendered
// through the sanctioned ContentView path — never raw injection), nested
// children lists indented below.
//
// Selectable variant only — the default viewType 'std' -> 'compact' mapping
// is unchanged (flip separately after browser review).

import { useState } from 'react'
import type { SubTemplateProps } from '@/deskUiSkeletons/rowTemplates/RowTemplateTypes'
import { ContentView, resolveContentDocument } from '@/app/richEdit/content'

function stripTags(value: string): string {
  return value.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()
}

export function LegacyStdTemplate({ sub, display, controls, slots }: SubTemplateProps) {
  const isMsg = sub.isMsg === true || display.state.section === 'submsges'
  const [moreOpen, setMoreOpen] = useState(false)
  const canViewContent = display.permissions.canViewContent && !display.access.isContentLocked

  // legacy has_more: the stored body carries more than TEXT_SHORT shows.
  const fullHtml = canViewContent && typeof sub.html === 'string' ? sub.html : ''
  const hasMore = fullHtml !== '' && stripTags(fullHtml).length > display.text.trim().length + 8
  const moreDoc = moreOpen ? resolveContentDocument({ bodyHtml: fullHtml }) : null

  const hasAvatar = Boolean(display.media.avatarPath)
  const hasAttachment = canViewContent && display.media.attachments.length > 0
  const hasEmail = Boolean(display.email.fromDisplay || display.email.to)

  return (
    <article className="md-legacystd-row clear-both mb-[6px] font-[Arial,Tahoma,Verdana,sans-serif] text-[12px] leading-[1.45] text-black" data-md-sub-template="legacystd">

      {isMsg ? (
        // ===== MESSAGE: checkbox + blue namekey strip =====
        <div className="mb-[2px] flex items-center gap-[5px]">
          <span className="[&_.md-sub-icon]:align-middle">{controls.check}</span>
          <div className="md-legacystd-title inline-block bg-[#3472a9] px-[8px] py-[3px] [&_.md-sub-title-link]:text-[12px] [&_.md-sub-title-link]:font-bold [&_.md-sub-title-link]:text-white [&_.md-sub-title-link]:no-underline hover:[&_.md-sub-title-link]:underline">
            {controls.title}
          </div>
          {display.isForeign ? (
            <span className="rounded-sm bg-[wheat] px-[5px] py-[1px] text-[11px] text-[#404040]" title={display.identity.address}>
              {display.identity.author}
            </span>
          ) : null}
        </div>
      ) : null}

      {/* ===== TEXT BLOCK (shared) ===== */}
      <div className="md-legacystd-block relative clear-both overflow-hidden bg-[#f5f5f5] p-[5px] pb-[7px] pr-[17px]">

        {hasAvatar ? (
          <img
            src={display.media.avatarPath}
            alt=""
            className="absolute left-[5px] top-[5px] h-[39px] w-[39px] bg-[silver] object-cover"
            onError={(event) => { event.currentTarget.style.display = 'none' }}
          />
        ) : null}

        {/* headline: icons + subject/link + right time group, #ddd underline */}
        <div className={`md-legacystd-headline overflow-hidden border-b border-[#ddd] pb-[4px] mb-[5px]${hasAvatar ? ' ml-[43px]' : ''}`}>
          {!isMsg ? (
            <span className="mr-[5px] inline-flex items-center gap-[4px] align-middle">
              {controls.check}
              {display.isForeign ? (
                <span className="rounded-sm bg-[wheat] px-[5px] py-[1px] text-[11px] text-[#404040]" title={display.identity.address}>
                  {display.identity.author}
                </span>
              ) : null}
            </span>
          ) : null}

          {hasEmail ? <span className="mr-[4px] text-[11px] text-[#777]" title={String(display.email.fromDisplay || '')}>✉</span> : null}
          {hasAttachment ? <span className="mr-[4px] text-[11px] text-[#777]" title={`${display.media.attachments.length} attachment(s)`}>📎</span> : null}

          {isMsg ? (
            <span className="md-legacystd-subject font-bold text-[#333]">{display.title}</span>
          ) : (
            <span className="[&_.md-sub-title-link]:font-bold [&_.md-sub-title-link]:text-[#3472a9] [&_.md-sub-title-link]:no-underline hover:[&_.md-sub-title-link]:underline">
              {controls.title}
            </span>
          )}

          {/* right group: fav + qr + time (legacy time_info wrapper) */}
          <span className="float-right inline-flex items-center gap-[5px] whitespace-nowrap">
            {controls.favorite}
            {controls.quickReply}
            <span className="text-[11px] text-[#888] [&_.md-sub-time]:text-[11px] [&_.md-sub-time]:text-[#888]">{controls.time}</span>
          </span>
        </div>

        {/* body: short text + read more / hide more */}
        <div className={`md-legacystd-text text-[12px]${hasAvatar ? ' ml-[43px]' : ''}`}>
          {!moreOpen ? (
            <>
              <span className="[&_.md-sub-text]:whitespace-normal">{controls.text}</span>
              {hasMore ? (
                <a
                  href="#"
                  className="ml-[5px] text-[11px] font-normal text-[#3472a9] underline"
                  onClick={(event) => { event.preventDefault(); setMoreOpen(true) }}
                >
                  ... read more
                </a>
              ) : null}
            </>
          ) : (
            <>
              {moreDoc ? <ContentView doc={moreDoc} /> : <span>{controls.text}</span>}
              <a
                href="#"
                className="ml-[5px] text-[11px] font-normal text-[#3472a9] underline"
                onClick={(event) => { event.preventDefault(); setMoreOpen(false) }}
              >
                Hide more
              </a>
            </>
          )}
        </div>
      </div>

      {/* ===== SUBS INFO BAR: answers (+ msges for named msges) ===== */}
      <div className="md-legacystd-subsbar mt-[3px] text-[11px] [&_a]:cursor-pointer [&_a]:text-[#3472a9] [&_span]:cursor-pointer">
        <span className="mr-[10px] inline-flex items-center gap-[8px]">
          {controls.links.answers}
          {isMsg ? controls.links.submsges : null}
        </span>
      </div>

      {/* ===== NESTED SUBS (legacy indent) ===== */}
      <div className="md-legacystd-children ml-[20px] clear-both">
        {slots.allChildren}
      </div>
    </article>
  )
}
